import type { PixelEvent } from '@/events';
import type { TikTokBrowserDestinationConfig } from '@/config/types/modules/integrations';

import { BaseDestinationAdapter } from '../../BaseDestinationAdapter';
import type { DestinationContext, DestinationResult } from '../../../types';
import {
  buildExternalPageViewKey,
  shouldSkipExternalPageView,
} from '../../../browser/ExternalPageViewDeduper';
import type { DestinationMetrics } from '../../../DestinationMetrics';
import { DestinationLogger } from '../../../DestinationLogger';
import { TikTokScriptLoader } from './TikTokScriptLoader';
import type { TikTokTtq } from './types';

export type TikTokBrowserDestinationDeps = {
  readonly config: TikTokBrowserDestinationConfig;
  readonly getSessionId: () => string;
  readonly logger: DestinationLogger;
  readonly metrics?: DestinationMetrics;
  readonly debug: boolean;
};

/**
 * TikTok Pixel browser destination — PAGE_VIEW only.
 *
 * Limitation: when only the global `ttq.page()` API exists (no `ttq.instance`),
 * a single page() call is issued per PAGE_VIEW event — not once per pixelId.
 * Multi-pixel dedupe still tracks per pixelId for the instance API path.
 */
export class TikTokBrowserDestination extends BaseDestinationAdapter {
  private readonly config: TikTokBrowserDestinationConfig;

  private readonly getSessionId: () => string;

  private readonly logger: DestinationLogger;

  private readonly metrics?: DestinationMetrics;

  private readonly debug: boolean;

  private readonly scriptLoader = new TikTokScriptLoader();

  private readonly pixelIds: string[];

  private initialized = false;

  private ttq: TikTokTtq | null = null;

  constructor(deps: TikTokBrowserDestinationDeps) {
    super({
      name: 'tiktok',
      version: '1.0.0',
      supportedTypes: new Set(['PAGE_VIEW']),
    });
    this.config = deps.config;
    this.getSessionId = deps.getSessionId;
    this.logger = deps.logger.forAdapter('TikTokBrowser');
    this.metrics = deps.metrics;
    this.debug = deps.debug;
    this.pixelIds = [...new Set(deps.config.pixelIds.filter(Boolean))];
  }

  supports(event: PixelEvent): boolean {
    return (
      this.config.enabled &&
      this.config.autoPageView !== false &&
      this.pixelIds.length > 0 &&
      event.type === 'PAGE_VIEW'
    );
  }

  async ensureInit(timeoutMs: number): Promise<void> {
    if (this.initialized) return;
    if (!this.config.enabled || this.pixelIds.length === 0) return;

    this.metrics?.recordInitAttempt('tiktok');
    if (this.debug) this.logger.debug('destination_init_start');

    if (this.config.loadScript !== false) {
      this.metrics?.recordScriptLoadAttempt('tiktok');
      if (this.debug) this.logger.debug('script_load_start');
      try {
        this.ttq = await this.scriptLoader.load(timeoutMs);
        this.metrics?.recordScriptLoadSuccess('tiktok');
        if (this.debug) this.logger.debug('script_load_success');
      } catch (error) {
        this.metrics?.recordScriptLoadFailure('tiktok');
        this.logger.error('script_load_failure', error);
        throw error;
      }
    } else if (
      typeof window !== 'undefined' &&
      (window as { ttq?: TikTokTtq }).ttq
    ) {
      this.ttq = (window as { ttq?: TikTokTtq }).ttq ?? null;
    }

    const ttq = this.ttq;
    if (!ttq) {
      this.metrics?.recordInitFailure('tiktok');
      throw new Error('[TikTokBrowser] ttq unavailable');
    }

    let initFailed = false;
    for (const pixelId of this.pixelIds) {
      try {
        ttq.load(pixelId);
      } catch (error) {
        initFailed = true;
        this.metrics?.recordInitFailure('tiktok');
        this.logger.error(`destination_init_failure id=${pixelId}`, error);
      }
    }

    this.initialized = true;
    if (!initFailed) this.metrics?.recordInitSuccess('tiktok');
    if (this.debug) this.logger.debug('destination_init_success');
  }

  async execute(context: DestinationContext): Promise<DestinationResult> {
    const startedAt = Date.now();
    const { event, signal, timeoutMs } = context;

    if (event.type !== 'PAGE_VIEW') {
      return this.createResult('ignored', Date.now() - startedAt, {
        ignored: true,
      });
    }

    if (signal.aborted) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'Aborted',
      });
    }

    if (this.debug) this.logger.debug('pageview_received');

    try {
      await this.ensureInit(timeoutMs);
    } catch (error) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    const ttq =
      this.ttq ??
      (typeof window !== 'undefined'
        ? ((window as { ttq?: TikTokTtq }).ttq ?? null)
        : null);
    if (!ttq) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'ttq unavailable',
      });
    }

    const path = event.path ?? event.url ?? '/';
    const sessionId = this.getSessionId();
    let sent = 0;
    let deduped = 0;

    const hasInstanceApi = typeof ttq.instance === 'function';

    if (hasInstanceApi) {
      for (const pixelId of this.pixelIds) {
        const key = buildExternalPageViewKey(
          'tiktok',
          pixelId,
          'PAGE_VIEW',
          path,
          sessionId
        );

        if (shouldSkipExternalPageView(key)) {
          deduped += 1;
          if (this.debug)
            this.logger.debug(`pageview_skipped_dedupe id=${pixelId}`);
          continue;
        }

        try {
          const instance = ttq.instance!(pixelId);
          instance.page();
          sent += 1;
          if (this.debug) this.logger.debug(`pageview_sent id=${pixelId}`);
        } catch (error) {
          this.logger.error(`destination_execute_failure id=${pixelId}`, error);
        }
      }
    } else {
      // Global ttq.page() — single call per PAGE_VIEW, not per pixelId.
      const dedupeId = this.pixelIds[0] ?? 'global';
      const key = buildExternalPageViewKey(
        'tiktok',
        dedupeId,
        'PAGE_VIEW',
        path,
        sessionId
      );

      if (shouldSkipExternalPageView(key)) {
        deduped = 1;
        if (this.debug) this.logger.debug('pageview_skipped_dedupe global');
      } else {
        try {
          ttq.page();
          sent = 1;
          if (this.debug) this.logger.debug('pageview_sent global');
        } catch (error) {
          this.logger.error('destination_execute_failure global', error);
        }
      }
    }

    if (sent === 0 && deduped > 0) {
      return this.createResult('skipped', Date.now() - startedAt, {
        skipped: true,
        metadata: { sent, deduped },
      });
    }

    return this.createResult('success', Date.now() - startedAt, {
      metadata: { sent, deduped, pixelIds: this.pixelIds },
    });
  }

  destroy(): void {
    this.scriptLoader.release();
    this.initialized = false;
    this.ttq = null;
  }
}
